// MyDialogButton.tsx - Modern & Beautiful
import { type ReactNode } from "react";
import { useMyDialog } from "../context/MyDialogProvider";
import { ChevronRight, Sparkles } from "lucide-react";

interface MyDialogButtonProps {
  label?: ReactNode;
  icon?: ReactNode;
  dialogContent: ReactNode;
  dialogHeader?: ReactNode | (() => ReactNode);
  variant?: "modal" | "sheet";
  padding?: string;
  rounded?: string;
  bgColor?: string;
  hoverColor?: string;
  textColor?: string;
  style?: string;
  textButtonClassName?: string;
  textButton?: boolean;
  topSmallLabel?: string;
  button?: ReactNode;
  route?: string;
  fullScreen?: boolean;
  initialSnap?: number;
  size?: "sm" | "md" | "lg";
  glow?: boolean;
  animated?: boolean;
  gradient?: boolean;
  iconPosition?: "left" | "right";
  className?: string;
  badge?: string | number;
  badgeColor?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

export default function MyDialogButton({
  label = "Open",
  variant = "sheet",
  fullScreen,
  textButton = false,
  textButtonClassName = "text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium transition-colors underline-offset-2 hover:underline",
  icon,
  dialogContent,
  dialogHeader,
  style = "",
  padding = "px-5 py-2.5",
  rounded = "rounded-xl",
  bgColor = "bg-gradient-to-r from-indigo-600 to-purple-600",
  hoverColor = "hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]",
  textColor = "text-white",
  topSmallLabel,
  button,
  route,
  initialSnap = 0.9,
  size = "md",
  glow = true,
  animated = true,
  gradient = true,
  iconPosition = "left",
  className = "",
  badge,
  badgeColor = "bg-rose-500",
  loading = false,
  disabled = false,
  onClick,
}: MyDialogButtonProps) {
  const { open } = useMyDialog();

  // Size configurations
  const sizeClasses = {
    sm: {
      padding: "px-3.5 py-1.5",
      text: "text-xs",
      icon: "w-3.5 h-3.5",
      rounded: "rounded-lg",
    },
    md: {
      padding: "px-5 py-2.5",
      text: "text-sm",
      icon: "w-4 h-4",
      rounded: "rounded-xl",
    },
    lg: {
      padding: "px-7 py-3.5",
      text: "text-base",
      icon: "w-5 h-5",
      rounded: "rounded-2xl",
    },
  };

  const sizeConfig = sizeClasses[size];

  const handleOpen = () => {
    if (disabled || loading) return;
    if (onClick) onClick();
    
    const header =
      typeof dialogHeader === "function" ? dialogHeader() : dialogHeader;

    open({
      variant,
      content: dialogContent,
      header,
      route,
      fullScreen,
      initialSnap,
    });
  };

  // Text button variant
  if (textButton) {
    return (
      <button
        type="button"
        className={`${textButtonClassName} ${loading ? 'opacity-50 cursor-wait' : ''} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
        onClick={handleOpen}
        disabled={disabled || loading}
        aria-haspopup="dialog"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading...
          </span>
        ) : (
          label
        )}
      </button>
    );
  }

  // Custom button variant
  if (button) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={(e) => e.key === "Enter" && handleOpen()}
        className={`cursor-pointer ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        aria-haspopup="dialog"
      >
        {button}
      </div>
    );
  }

  // Determine gradient class
  const gradientClass = gradient 
    ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500" 
    : bgColor;

  // Glow effect
  const glowClass = glow 
    ? "shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)]" 
    : "shadow-md";

  // Animation
  const animationClass = animated 
    ? "transition-all duration-300 ease-out transform" 
    : "";

  // Badge
  const badgeElement = badge && (
    <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 ${badgeColor} text-white text-[10px] font-bold rounded-full px-1.5 flex items-center justify-center shadow-lg animate-pulse`}>
      {badge}
    </span>
  );

  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={disabled || loading}
      aria-haspopup="dialog"
      className={`
        relative
        flex items-center gap-2.5
        font-medium
        ${sizeConfig.text}
        ${sizeConfig.padding}
        ${sizeConfig.rounded}
        ${gradientClass}
        ${textColor}
        ${hoverColor}
        ${glowClass}
        ${animationClass}
        ${className}
        ${style}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${loading ? 'cursor-wait' : ''}
        border border-white/10
        backdrop-blur-sm
        overflow-hidden
        group
      `}
    >
      {/* Background shine effect */}
      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      {/* Shimmer border effect */}
      <span className="absolute inset-0 rounded-[inherit] p-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Top small label */}
      {topSmallLabel && (
        <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
          {topSmallLabel}
        </span>
      )}

      {/* Badge */}
      {badgeElement}

      {/* Loading spinner */}
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center bg-inherit rounded-[inherit]">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </span>
      )}

      {/* Content */}
      <span className={`flex items-center gap-2.5 ${loading ? 'opacity-0' : ''}`}>
        {iconPosition === "left" && icon && (
          <span className={`${sizeConfig.icon} flex-shrink-0`}>{icon}</span>
        )}
        
        <span className="relative">
          {label}
          {/* Underline hover effect */}
          <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white/50 group-hover:w-full transition-all duration-300" />
        </span>
        
        {iconPosition === "right" && icon && (
          <span className={`${sizeConfig.icon} flex-shrink-0`}>{icon}</span>
        )}

        {/* Arrow indicator */}
        <ChevronRight className={`${sizeConfig.icon} flex-shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-300 ${iconPosition === "right" ? 'order-last' : ''}`} />
      </span>

      {/* Sparkle decoration */}
      <Sparkles className={`absolute -bottom-1 -right-1 w-8 h-8 text-white/5 group-hover:text-white/10 transition-colors duration-500 rotate-12`} />
    </button>
  );
}